import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { OrderDialog } from "@/components/admin/dashboard/OrderDialog";
import { StatusChangeDialog } from "@/components/admin/dashboard/StatusChangeDialog";
import { DashboardStats } from "@/components/admin/dashboard/DashboardStats";
import { ContactsTable } from "@/components/admin/dashboard/ContactsTable";
import { OrdersTab } from "@/components/admin/dashboard/OrdersTab";
import { RecommendationsTab } from "@/components/admin/dashboard/RecommendationsTab";
import { TrialSessionsTab } from "@/components/admin/dashboard/TrialSessionsTab";
import { CouponsTab } from "@/components/admin/dashboard/CouponsTab";
import { StatusHistoryDialog } from "@/components/admin/dashboard/StatusHistoryDialog";
import { DeleteConfirmDialog } from "@/components/admin/dashboard/DeleteConfirmDialog";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useStatusManagement } from "@/hooks/useStatusManagement";
import { getStatusColor } from "@/utils/statusColors";

export const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab');

  const {
    loading,
    contacts,
    setContacts,
    orders,
    setOrders,
    mentorApplications,
    coachRecommendations,
    setCoachRecommendations,
    coupons,
    setCoupons,
    trialSessions,
    setTrialSessions,
  } = useDashboardData();

  const {
    statusChangeNotes,
    setStatusChangeNotes,
    showStatusDialog,
    setShowStatusDialog,
    showTrialStatusDialog,
    setShowTrialStatusDialog,
    showStatusHistory,
    setShowStatusHistory,
    statusHistory,
    selectedRecommendation,
    setSelectedRecommendation,
    selectedTrialSession,
    setSelectedTrialSession,
    newStatus,
    setNewStatus,
    handleStatusChange,
    handleTrialStatusChange,
    fetchStatusHistory,
    fetchTrialStatusHistory,
  } = useStatusManagement(coachRecommendations, setCoachRecommendations, trialSessions, setTrialSessions);

  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteContactConfirm, setShowDeleteContactConfirm] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [showDeleteRecommendationConfirm, setShowDeleteRecommendationConfirm] = useState(false);
  const [showDeleteTrialConfirm, setShowDeleteTrialConfirm] = useState(false);

  if (loading) {
    return <div className="p-8 pt-24">Yükleniyor...</div>;
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        
        <div className="flex-1 p-8 pt-24">
          <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>

          {currentTab === 'analytics' ? (
            <AnalyticsTab />
          ) : (
            <>
              <DashboardStats
                contacts={contacts}
                orders={orders}
                mentorApplications={mentorApplications}
                coachRecommendations={coachRecommendations}
                coupons={coupons}
              />

              <Tabs defaultValue="contacts" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="contacts">İletişim Formları</TabsTrigger>
                  <TabsTrigger value="orders">Siparişler</TabsTrigger>
                  <TabsTrigger value="applications">Mentor Başvuruları</TabsTrigger>
                  <TabsTrigger value="recommendations">Koç Önerileri</TabsTrigger>
                  <TabsTrigger value="trials">Ücretsiz Görüşmeler</TabsTrigger>
                  <TabsTrigger value="coupons">Kuponlar</TabsTrigger>
                </TabsList>

                <TabsContent value="contacts">
                  <ContactsTable
                    contacts={contacts}
                    onDelete={(contact) => {
                      setSelectedContact(contact);
                      setShowDeleteContactConfirm(true);
                    }}
                  />
                </TabsContent>

                <TabsContent value="orders">
                  <OrdersTab
                    orders={orders}
                    onViewDetails={setSelectedOrder}
                  />
                </TabsContent>

                <TabsContent value="recommendations">
                  <RecommendationsTab
                    recommendations={coachRecommendations}
                    getStatusColor={getStatusColor}
                    onStatusChange={(rec) => {
                      setSelectedRecommendation(rec);
                      setShowStatusDialog(true);
                    }}
                    onHistoryView={fetchStatusHistory}
                    onDelete={(rec) => {
                      setSelectedRecommendation(rec);
                      setShowDeleteRecommendationConfirm(true);
                    }}
                  />
                </TabsContent>

                <TabsContent value="trials">
                  <TrialSessionsTab
                    trialSessions={trialSessions}
                    getStatusColor={getStatusColor}
                    onStatusChange={(trial) => {
                      setSelectedTrialSession(trial);
                      setShowTrialStatusDialog(true);
                    }}
                    onHistoryView={fetchTrialStatusHistory}
                    onDelete={(trial) => {
                      setSelectedTrialSession(trial);
                      setShowDeleteTrialConfirm(true);
                    }}
                  />
                </TabsContent>

                <TabsContent value="coupons">
                  <CouponsTab
                    coupons={coupons}
                    onNewCoupon={() => {}} // TODO: Implement new coupon functionality
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>

        <OrderDialog
          selectedOrder={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onDelete={() => setShowDeleteConfirm(true)}
        />

        <StatusChangeDialog
          open={showStatusDialog}
          onOpenChange={setShowStatusDialog}
          selectedItem={selectedRecommendation}
          statusChangeNotes={statusChangeNotes}
          onNotesChange={setStatusChangeNotes}
          newStatus={newStatus}
          onStatusChange={setNewStatus}
          onSave={handleStatusChange}
          type="recommendation"
        />

        <StatusChangeDialog
          open={showTrialStatusDialog}
          onOpenChange={setShowTrialStatusDialog}
          selectedItem={selectedTrialSession}
          statusChangeNotes={statusChangeNotes}
          onNotesChange={setStatusChangeNotes}
          newStatus={newStatus}
          onStatusChange={setNewStatus}
          onSave={handleTrialStatusChange}
          type="trial"
        />

        <StatusHistoryDialog
          open={showStatusHistory}
          onOpenChange={setShowStatusHistory}
          statusHistory={statusHistory}
        />

        <DeleteConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title="Siparişi Silmeyi Onayla"
          description="Bu işlem geri alınamaz. Siparişi silmek istediğinizden emin misiniz?"
          onConfirm={() => {}} // TODO: Implement delete functionality
        />

        <DeleteConfirmDialog
          open={showDeleteContactConfirm}
          onOpenChange={setShowDeleteContactConfirm}
          title="İletişim Kaydını Sil"
          description="Bu işlem geri alınamaz. İletişim kaydını silmek istediğinizden emin misiniz?"
          onConfirm={() => {}} // TODO: Implement delete functionality
        />

        <DeleteConfirmDialog
          open={showDeleteRecommendationConfirm}
          onOpenChange={setShowDeleteRecommendationConfirm}
          title="Koç Önerisi Kaydını Sil"
          description="Bu işlem geri alınamaz. Koç önerisi kaydını silmek istediğinizden emin misiniz?"
          onConfirm={() => {}} // TODO: Implement delete functionality
        />

        <DeleteConfirmDialog
          open={showDeleteTrialConfirm}
          onOpenChange={setShowDeleteTrialConfirm}
          title="Ücretsiz Görüşme Kaydını Sil"
          description="Bu işlem geri alınamaz. Ücretsiz görüşme kaydını silmek istediğinizden emin misiniz?"
          onConfirm={() => {}} // TODO: Implement delete functionality
        />
      </div>
    </SidebarProvider>
  );
};

export default AdminDashboard;
